import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Loads the cron-refreshed AI-generated ecommerce search suggestion pool once
 * on mount and caches it in localStorage. The cache TTL is admin-configurable
 * via ai_suggestion_config.client_cache_ttl_minutes (default 6 h), fetched
 * lazily from the public-safe getter RPC.
 *
 * The actual AI model call happens server-side (cron). Clients never call
 * the AI gateway directly.
 */
const STORAGE_KEY = "askthepods.aiSuggestionCache.v1";
const CONFIG_KEY = "askthepods.aiSuggestionConfig.v1";
const FALLBACK_TTL_MS = 6 * 60 * 60 * 1000;
const CONFIG_TTL_MS = 60 * 60 * 1000; // re-check tunables hourly

interface StoredCache {
  suggestions: string[];
  fetchedAt: number;
}

interface StoredConfig {
  ttlMs: number;
  fetchedAt: number;
}

function readConfigTtl(): number {
  if (typeof window === "undefined") return FALLBACK_TTL_MS;
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return FALLBACK_TTL_MS;
    const parsed = JSON.parse(raw) as StoredConfig;
    if (Date.now() - parsed.fetchedAt > CONFIG_TTL_MS) return FALLBACK_TTL_MS;
    return parsed.ttlMs || FALLBACK_TTL_MS;
  } catch {
    return FALLBACK_TTL_MS;
  }
}

async function refreshConfigTtl(): Promise<number> {
  try {
    const { data } = await supabase.rpc("get_ai_suggestion_public_config");
    const row = Array.isArray(data) ? data[0] : data;
    const minutes = Number(row?.client_cache_ttl_minutes);
    const ttlMs = Number.isFinite(minutes) && minutes > 0 ? minutes * 60 * 1000 : FALLBACK_TTL_MS;
    try {
      localStorage.setItem(CONFIG_KEY, JSON.stringify({ ttlMs, fetchedAt: Date.now() } as StoredConfig));
    } catch { /* ignore */ }
    return ttlMs;
  } catch {
    return FALLBACK_TTL_MS;
  }
}

export function useAiSuggestionPool(): string[] {
  const [pool, setPool] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as StoredCache;
      if (Date.now() - parsed.fetchedAt > readConfigTtl()) return [];
      return Array.isArray(parsed.suggestions) ? parsed.suggestions : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ttlMs = await refreshConfigTtl();

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as StoredCache;
          if (Date.now() - parsed.fetchedAt < ttlMs && Array.isArray(parsed.suggestions)) {
            return;
          }
        }
      } catch { /* fall through to fetch */ }

      const { data } = await supabase
        .from("ai_suggestion_cache")
        .select("suggestions")
        .eq("bucket", "global")
        .maybeSingle();

      if (cancelled) return;
      const suggestions = Array.isArray(data?.suggestions)
        ? (data!.suggestions as string[]).filter((s) => typeof s === "string")
        : [];
      if (suggestions.length === 0) return;
      setPool(suggestions);
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ suggestions, fetchedAt: Date.now() } as StoredCache),
        );
      } catch { /* storage may be full / private mode */ }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return pool;
}
